/// <amd-module name="VSSUI/Components/Dropdown/WithPivots" />
import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { FocusZone, FocusZoneContext, FocusZoneDirection, IFocusZoneContext } from "VSSUI/FocusZone";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { DropdownCallout } from "./DropdownCallout";
import { IDropdownCalloutProps } from "./DropdownCallout.Props";
import { IDropdownPivot, IWithPivotsChildProps, IWithPivotsProps } from "./WithPivots.Props";

export function WithPivots<T = {}>(props: IWithPivotsProps<T>) {
    const renderCallout = (calloutProps: IDropdownCalloutProps) => {
        return (
            <DropdownCallout
                {...calloutProps}
                {...props.calloutProps}
                onFilterTextChanged={(e, value) => {
                    calloutProps.onFilterTextChanged && calloutProps.onFilterTextChanged(e, value);
                    props.calloutProps && props.calloutProps.onFilterTextChanged && props.calloutProps.onFilterTextChanged(e, value);
                }}
                renderBeforeContent={() => renderPivots(props)}
            />
        );
    };

    return (
        <Observer selectedPivot={props.selectedPivot}>
            {() => {
                const selectedPivot = getSelectedPivot(props);
                return (
                    <Observer userFilteredItems={selectedPivot.userFilteredItems}>
                        {() => {
                            const newProps: IWithPivotsChildProps = {
                                actions: selectedPivot.actions,
                                filterByText: selectedPivot.filterByText,
                                userFilteredItems: selectedPivot.userFilteredItems || selectedPivot.items,
                                filteredNoResultsText: selectedPivot.filteredNoResultsText,
                                filterPlaceholderText: selectedPivot.filterPlaceholderText,
                                noItemsText: selectedPivot.noItemsText,
                                renderCallout: renderCallout,
                                showFilterBox: selectedPivot.showFilterBox
                            };
                            return props.children(newProps);
                        }}
                    </Observer>
                );
            }}
        </Observer>
    );
}

function onPivotClick<T>(pivot: IDropdownPivot<T>, props: IWithPivotsProps<T>) {
    if (props.onPivotClicked) {
        props.onPivotClicked(pivot);
    }
}

function getSelectedPivot<T>(props: IWithPivotsProps<T>): IDropdownPivot<T> {
    let selectedPivot;
    if (props.selectedPivot) {
        const selectedPivotId = ObservableLike.getValue(props.selectedPivot);
        selectedPivot = props.pivots.find(pivot => pivot.id === selectedPivotId);
    }
    return selectedPivot || props.pivots[0];
}

function renderPivots<T>(props: IWithPivotsProps<T>): JSX.Element | null {
    if (props.pivots.length < 2) {
        return null;
    }
    return (
        <FocusZoneContext.Consumer>
            {(verticalContext: IFocusZoneContext) => (
                <FocusZone direction={FocusZoneDirection.Horizontal}>
                    <div role="tablist">
                        <FocusWithin>
                            {(focusStatus: IFocusWithinStatus) => {
                                return props.pivots.map((pivot, index) => {
                                    const selected = props.selectedPivot !== undefined && pivot.id === ObservableLike.getValue(props.selectedPivot);
                                    return (
                                        <Button
                                            ariaSelected={selected}
                                            ariaSetSize={props.pivots.length}
                                            ariaPosInSet={index + 1}
                                            className={"bolt-dropdown-pivot"}
                                            key={pivot.id}
                                            subtle={true}
                                            onBlur={focusStatus.onBlur}
                                            onClick={() => onPivotClick<T>(pivot, props)}
                                            onFocus={focusStatus.onFocus}
                                            role="tab"
                                            focusZoneId={index === 0 && !focusStatus.hasFocus ? verticalContext.focuszoneId : undefined}
                                        >
                                            <span
                                                className={css(
                                                    props.selectedPivot &&
                                                        pivot.id === ObservableLike.getValue(props.selectedPivot) &&
                                                        "bolt-dropdown-pivot-selected"
                                                )}
                                            >
                                                {pivot.name || pivot.id}
                                            </span>
                                        </Button>
                                    );
                                });
                            }}
                        </FocusWithin>
                    </div>
                </FocusZone>
            )}
        </FocusZoneContext.Consumer>
    );
}
